import {
  canResendLink,
  capitalizeFirst,
  formatDate,
  formatDateTime,
  getPolicyRowId,
} from "partnerportal/helpers/utils";
import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MdEdit, MdOutlineForwardToInbox } from "react-icons/md";
import {
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner,
  Table,
} from "reactstrap";

const tooltipTriggerStyle = {
  cursor: "default",
};

const tooltipBoxStyle = {
  position: "fixed",
  zIndex: 9999,
  backgroundColor: "#333",
  color: "#fff",
  borderRadius: "6px",
  padding: "6px 10px",
  whiteSpace: "nowrap",
  fontSize: "12px",
  lineHeight: "1.8",
  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  pointerEvents: "none",
  maxHeight: "160px",
  overflowY: "auto",
  minWidth: "140px",
};

const centeredHeaderStyle = {
  textAlign: "center",
};

const TAG_ROW_HEIGHT = 22; // approx px per tag line, used to size the gap estimate
const TOOLTIP_MARGIN = 8;

const TagTooltip = ({ anchorRect, tags, values }) => {
  if (!anchorRect) return null;

  const estimatedHeight = Math.min(tags.length * TAG_ROW_HEIGHT + 12, 160);

  // Prefer showing above the trigger; flip below if there isn't room.
  const spaceAbove = anchorRect.top;
  const showAbove = spaceAbove > estimatedHeight + TOOLTIP_MARGIN;

  const style = {
    ...tooltipBoxStyle,
    left: Math.max(8, anchorRect.left + anchorRect.width / 2),
    transform: "translateX(-50%)",
    ...(showAbove
      ? { bottom: window.innerHeight - anchorRect.top + TOOLTIP_MARGIN }
      : { top: anchorRect.bottom + TOOLTIP_MARGIN }),
  };

  return createPortal(
    <div style={style} className="tag-tooltip-portal">
      {tags.map((tag, i) => (
        <span key={`${tag}-${i}`} style={{ display: "block" }}>
          {tag} &mdash; &#8377;
          {values?.[i] != null
            ? Number(values[i]).toLocaleString("en-IN")
            : "-"}
        </span>
      ))}
    </div>,
    document.body
  );
};

const PoliciesTable = ({
  policies,
  currentPage,
  totalPages,
  totalRecords,
  itemsPerPage,
  onPageChange,
  resendingPolicyId,
  onResend,
  onEdit,
  hideActions = false,
  showCompany = false,
}) => {
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const triggerRefs = useRef({});

  const startRecord = (currentPage - 1) * itemsPerPage + 1;
  const endRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  const pageWindow = (() => {
    const delta = 2;
    const range = [];
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      range.push(i);
    }
    return range;
  })();

  const handleMouseEnter = (rowId) => {
    const el = triggerRefs.current[rowId];
    if (el) {
      setAnchorRect(el.getBoundingClientRect());
      setHoveredRowId(rowId);
    }
  };

  const handleMouseLeave = () => {
    setHoveredRowId(null);
    setAnchorRect(null);
  };

  return (
    <>
      <div className="table-responsive">
        <Table className="table align-middle table-nowrap mb-0">
          <thead className="table-light">
            <tr>
              <th style={centeredHeaderStyle}>Contract No</th>
              {showCompany && (
                <th style={centeredHeaderStyle}>Company Name</th>
              )}
              <th style={centeredHeaderStyle}>No of Bags</th>
              <th style={centeredHeaderStyle}>Customer Name</th>
              <th style={centeredHeaderStyle}>Email</th>
              <th style={centeredHeaderStyle}>Start Date</th>
              <th style={centeredHeaderStyle}>Expiry Date</th>
              <th style={centeredHeaderStyle}>Created Date</th>
              <th style={centeredHeaderStyle}>Created By</th>
              <th style={centeredHeaderStyle}>Status</th>
              {!hideActions && <th style={centeredHeaderStyle}>Edit</th>}
            </tr>
          </thead>
          <tbody>
            {policies.map((item) => {
              const rowId = getPolicyRowId(item);
              const isResending = resendingPolicyId === item._id;
              const hasTagNumbers =
                Array.isArray(item.tag_numbers) && item.tag_numbers.length > 0;

              return (
                <tr key={rowId}>
                  <td>{item.policy_number || "-"}</td>
                  {showCompany && (
                    <td>
                      {item.company_name ||
                        (item.company_uid === "tag8 default"
                          ? "Tag8 Default"
                          : item.company_uid) ||
                        "-"}
                    </td>
                  )}

                  {/* ── No of Bags with hover tooltip ── */}
                  <td>
                    {hasTagNumbers ? (
                      <span
                        ref={(el) => (triggerRefs.current[rowId] = el)}
                        style={tooltipTriggerStyle}
                        onMouseEnter={() => handleMouseEnter(rowId)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {item.no_of_bags ?? item.tag_numbers.length}
                        {hoveredRowId === rowId && (
                          <TagTooltip
                            anchorRect={anchorRect}
                            tags={item.tag_numbers}
                            values={item.insurance_values}
                          />
                        )}
                      </span>
                    ) : (
                      item.no_of_bags ?? 1
                    )}
                  </td>

                  <td>
                    {[item.first_name, item.last_name]
                      .filter(Boolean)
                      .join(" ") || "-"}
                  </td>
                  <td>{item.email || "-"}</td>
                  <td>{formatDate(item.insurance_coverage_startdate)}</td>
                  <td>{formatDate(item.bag_insurance_expiry_date)}</td>
                  <td>{formatDateTime(item.create_date)}</td>
                  <td>{item.created_by || "-"}</td>
                  <td>
                    <div>
                      {item.tag_status?.toLowerCase() === "inactive"
                        ? "Customer Details Pending"
                        : capitalizeFirst(item.tag_status)}
                    </div>
                    {canResendLink(item) && !hideActions && (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        onClick={() => onResend(item)}
                        disabled={isResending}
                        title={
                          isResending ? "Sending..." : "Resend activation link"
                        }
                      >
                        {isResending ? (
                          <Spinner size="sm" />
                        ) : (
                          <MdOutlineForwardToInbox size={18} />
                        )}
                      </Button>
                    )}
                  </td>
                  {!hideActions && (
                    <td>
                      {canResendLink(item) ? (
                        <Button
                          color="link"
                          size="sm"
                          className="p-0"
                          onClick={() => onEdit?.(item)}
                          title="Edit policy details"
                        >
                          <MdEdit size={18} />
                        </Button>
                      ) : (
                        "-"
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
        <div className="text-muted">
          Showing {startRecord}–{endRecord} of {totalRecords}
        </div>

        {totalPages > 1 && (
          <Pagination className="pagination-rounded mb-0">
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink
                previous
                onClick={() => onPageChange(currentPage - 1)}
                style={{ cursor: "pointer" }}
              />
            </PaginationItem>

            {pageWindow[0] > 1 && (
              <>
                <PaginationItem>
                  <PaginationLink
                    onClick={() => onPageChange(1)}
                    style={{ cursor: "pointer" }}
                  >
                    1
                  </PaginationLink>
                </PaginationItem>
                {pageWindow[0] > 2 && (
                  <PaginationItem disabled>
                    <PaginationLink>…</PaginationLink>
                  </PaginationItem>
                )}
              </>
            )}

            {pageWindow.map((page) => (
              <PaginationItem active={page === currentPage} key={page}>
                <PaginationLink
                  onClick={() => onPageChange(page)}
                  style={{ cursor: "pointer" }}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}

            {pageWindow[pageWindow.length - 1] < totalPages && (
              <>
                {pageWindow[pageWindow.length - 1] < totalPages - 1 && (
                  <PaginationItem disabled>
                    <PaginationLink>…</PaginationLink>
                  </PaginationItem>
                )}
                <PaginationItem>
                  <PaginationLink
                    onClick={() => onPageChange(totalPages)}
                    style={{ cursor: "pointer" }}
                  >
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              </>
            )}

            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink
                next
                onClick={() => onPageChange(currentPage + 1)}
                style={{ cursor: "pointer" }}
              />
            </PaginationItem>
          </Pagination>
        )}
      </div>
    </>
  );
};

export default PoliciesTable;
